import { CAPITAL_ACCOUNT_ROWS } from "./capitalAccountRows";
import { getVal, isEmpty, periodKey, sortPeriods } from "./utils";
import type { PeRow } from "./utils";

type AuditScalar = string | number | boolean | null | undefined;

// ─── Types ────────────────────────────────────────────────────────────────────

export type ChangeKind =
  | "became_empty" // was filled, now empty
  | "became_filled" // was empty, now filled
  | "numeric_jump" // big numeric delta
  | "changed"; // simple text change

export interface AuditChange {
  key: string;
  label: string;
  kind: ChangeKind;
  before: string;
  after: string;
  deltaPct?: number; // relative change (not fractional: 1.0 = 100%)
}

export interface EmptyFlag {
  key: string;
  label: string;
  value: string;
}

export interface SourceDiff {
  key: string;
  label: string;
  values: { docLabel: string; value: string }[];
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function toAuditString(v: AuditScalar): string {
  if (v === null || v === undefined) return "";
  if (typeof v === "string") return v;
  if (typeof v === "number" || typeof v === "boolean") return String(v);
  return "";
}

/** Parse a raw value into a number. Handles "1,234.5", "10%", "2.5x", etc. */
export function parseNum(v: AuditScalar): number | null {
  const raw = toAuditString(v).trim();
  if (
    raw === "" ||
    raw.toLowerCase() === "nan" ||
    raw.toLowerCase() === "none"
  ) {
    return null;
  }
  const cleaned = raw
    .replace(/,/g, "")
    .replace(/%/g, "")
    .replace(/x$/i, "")
    .trim();
  const n = parseFloat(cleaned);
  return isNaN(n) ? null : n;
}

/** Compute relative change |b-a|/max(|a|,|b|). Returns null if both 0 or invalid. */
export function relDelta(a: number, b: number): number | null {
  const denom = Math.max(Math.abs(a), Math.abs(b));
  if (denom === 0) return null;
  return Math.abs(b - a) / denom;
}

// ─── FO / Portfolio General Info — fill-state change detector ────────────────

/**
 * Compare two rows for a set of tracked keys. Surfaces only fill-state
 * transitions (empty → filled, filled → empty) and textual changes.
 * Numeric fields still appear as "changed" when text differs.
 */
export function diffFillState(
  before: PeRow | null,
  after: PeRow | null,
  fields: { key: string; label: string }[],
): AuditChange[] {
  if (!before || !after) return [];
  const changes: AuditChange[] = [];
  for (const { key, label } of fields) {
    const vb = getVal(before, key);
    const va = getVal(after, key);
    const emptyB = isEmpty(vb);
    const emptyA = isEmpty(va);
    if (emptyB && emptyA) continue;
    if (!emptyB && emptyA) {
      changes.push({ key, label, kind: "became_empty", before: vb, after: "" });
    } else if (emptyB && !emptyA) {
      changes.push({
        key,
        label,
        kind: "became_filled",
        before: "",
        after: va,
      });
    } else if (vb !== va) {
      changes.push({ key, label, kind: "changed", before: vb, after: va });
    }
  }
  return changes;
}

// ─── Portfolio positions — numeric change detector ───────────────────────────

export interface NumericChange extends AuditChange {
  kind: "numeric_jump" | "became_empty" | "became_filled";
  beforeNum?: number;
  afterNum?: number;
}

/**
 * Compare two numeric values; returns a change entry iff:
 *  - one side became empty / filled, OR
 *  - both are numeric AND relDelta exceeds threshold.
 */
export function compareNumericField(
  key: string,
  label: string,
  before: AuditScalar,
  after: AuditScalar,
  threshold: number,
): NumericChange | null {
  const beforeStr = toAuditString(before);
  const afterStr = toAuditString(after);
  const eb = isEmpty(beforeStr);
  const ea = isEmpty(afterStr);
  if (eb && ea) return null;
  if (!eb && ea)
    return { key, label, kind: "became_empty", before: beforeStr, after: "" };
  if (eb && !ea)
    return { key, label, kind: "became_filled", before: "", after: afterStr };

  const nb = parseNum(beforeStr);
  const na = parseNum(afterStr);
  if (nb === null || na === null) return null;
  const d = relDelta(nb, na);
  if (d === null) return null;
  if (d < threshold) return null;
  const signedDelta =
    Math.max(Math.abs(nb), Math.abs(na)) === 0
      ? 0
      : (na - nb) / Math.max(Math.abs(nb), Math.abs(na));
  return {
    key,
    label,
    kind: "numeric_jump",
    before: beforeStr,
    after: afterStr,
    beforeNum: nb,
    afterNum: na,
    deltaPct: signedDelta,
  };
}

// ─── Empty-value flags (critical fields) ─────────────────────────────────────

export function flagEmpty(
  row: PeRow,
  fields: { key: string; label: string }[],
): EmptyFlag[] {
  const out: EmptyFlag[] = [];
  for (const { key, label } of fields) {
    const v = getVal(row, key);
    if (isEmpty(v)) out.push({ key, label, value: "" });
  }
  return out;
}

// ─── Capital Account — same-period cross-source diff ─────────────────────────

/**
 * Compare CA values across multiple docs (= multiple sources for the same period).
 * Surfaces rows where at least two sources have non-empty, non-equal values.
 */
export function diffCrossSource(
  docRows: { docId: string; docLabel: string; row: PeRow | null }[],
  threshold: number,
): SourceDiff[] {
  const out: SourceDiff[] = [];
  const numericKeys = CAPITAL_ACCOUNT_ROWS.filter(
    (r) => r.type !== "section" && (r.isNum || r.isPct),
  );
  for (const cfg of numericKeys) {
    const keys = [cfg.keyF!];
    if (cfg.keyS && !cfg.isSingle) keys.push(cfg.keyS);
    for (const key of keys) {
      const suffix = key.endsWith("_share") ? " (share)" : " (fund)";
      const label = `${cfg.label}${cfg.keyS && !cfg.isSingle ? suffix : ""}`;
      const values = docRows
        .map((d) => ({
          docLabel: d.docLabel,
          value: d.row ? getVal(d.row, key) : "",
        }))
        .filter((v) => !isEmpty(v.value));
      if (values.length < 2) continue;
      // Find any pair with numeric delta > threshold, or any textual mismatch.
      const nums = values.map((v) => parseNum(v.value));
      let flag = false;
      if (nums.every((n) => n !== null)) {
        const mn = Math.min(...(nums as number[]));
        const mx = Math.max(...(nums as number[]));
        const d = relDelta(mn, mx);
        if (d !== null && d >= threshold) flag = true;
      } else {
        const uniq = new Set(values.map((v) => v.value));
        if (uniq.size > 1) flag = true;
      }
      if (flag) out.push({ key, label, values });
    }
  }
  return out;
}

// ─── Capital Account — quarter-to-quarter delta (numeric fields) ─────────────

export function diffPeriodOverPeriod(
  before: PeRow | null,
  after: PeRow | null,
  threshold: number,
): NumericChange[] {
  if (!before || !after) return [];
  const changes: NumericChange[] = [];
  for (const cfg of CAPITAL_ACCOUNT_ROWS) {
    if (cfg.type === "section") continue;
    if (!(cfg.isNum || cfg.isPct)) continue;
    const pairs = [cfg.keyF!];
    if (cfg.keyS && !cfg.isSingle) pairs.push(cfg.keyS);
    for (const key of pairs) {
      const suffix = key.endsWith("_share") ? " (share)" : " (fund)";
      const label = `${cfg.label}${cfg.keyS && !cfg.isSingle ? suffix : ""}`;
      const c = compareNumericField(
        key,
        label,
        getVal(before, key),
        getVal(after, key),
        threshold,
      );
      if (c) changes.push(c);
    }
  }
  return changes;
}

// ─── Sort helpers ─────────────────────────────────────────────────────────────

/** Sort doc IDs newest → oldest by reporting_period. */
export function sortDocIdsByPeriodDesc(
  docIds: string[],
  docMeta: Record<string, { reporting_period: string }>,
): string[] {
  const periods = docIds.map((d) => docMeta[d]?.reporting_period ?? "");
  const sorted = sortPeriods(periods).reverse();
  // Map back to docIds (may have duplicates if same period — keep order).
  const result: string[] = [];
  const used = new Set<string>();
  for (const p of sorted) {
    const match = docIds.find(
      (d) => !used.has(d) && docMeta[d]?.reporting_period === p,
    );
    if (match) {
      result.push(match);
      used.add(match);
    }
  }
  for (const d of docIds) if (!used.has(d)) result.push(d);
  return result;
}

/** Group doc IDs by period key. */
export function groupByPeriod(
  docIds: string[],
  docMeta: Record<string, { reporting_period: string }>,
): Map<string, string[]> {
  const map = new Map<string, string[]>();
  for (const d of docIds) {
    const p = docMeta[d]?.reporting_period;
    if (!p) continue;
    const k = periodKey(p);
    if (!map.has(k)) map.set(k, []);
    map.get(k)!.push(d);
  }
  return map;
}
